import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTasks } from '../TaskProvider'
import { useRefreshGuard } from '../refreshGuards'
import { roomRequest } from '../roomApi'
import { referenceTargets } from '../connectedBible'
import { taskAncestors } from '../taskTree'
import { draftTask } from '../taskOrganization'
import { TaskOrganizer } from './TaskOrganizer'
import { useRouteState } from '../routeState'
import type { ProjectEnvelope, ProjectSummary, StoryProject, WritingTask } from '../types'

export type TaskDraft = { task?: WritingTask; parent?: WritingTask; kind?: 'task' | 'group'; scope: 'team' | 'personal'; serial: number }
export function TaskEditor({ draft, project, projects, onSave, onCancel, error, focus = true }: {
  draft: TaskDraft
  project: StoryProject | null
  projects: ProjectSummary[]
  onSave: (body: Partial<WritingTask>) => Promise<void>
  onCancel: () => void
  error: string
  focus?: boolean
}) {
  const store = useTasks()
  useRefreshGuard(true, 'Finish or cancel your task before refreshing.')
  const task = draft.task
  const kind = task?.kind || draft.kind || 'task'
  const [title, setTitle] = useState(task?.title || '')
  const [details, setDetails] = useState(task?.details || '')
  const [scope, setScope] = useState(task?.scope || draft.parent?.scope || draft.scope)
  const [parentId, setParentId] = useState(task?.parentId || draft.parent?.id || '')
  const [assigneeId, setAssigneeId] = useState(task?.assigneeId ?? store.user.id)
  const [dueDate, setDueDate] = useState(task?.dueDate || '')
  const [projectId, setProjectId] = useState(task?.projectId || draft.parent?.projectId || '')
  const [targetId, setTargetId] = useState(task?.targetId || draft.parent?.targetId || '')
  const [linkMode, setLinkMode] = useState<WritingTask['linkMode']>(task?.linkMode || (task?.projectId ? 'manual' : 'inherit'))
  const [chooseParent, setChooseParent] = useRouteState<boolean>('task-parent', false)
  const [linked, setLinked] = useState<StoryProject | null>(null)
  const [referenceError, setReferenceError] = useState('')
  const parent = store.tasks.find(t => t.id === parentId)
  const inherited = linkMode === 'inherit' && !task?.reference
  const shownProjectId = inherited ? parent?.projectId || '' : projectId
  const shownTargetId = inherited ? parent?.targetId || '' : targetId
  const form = useRef<HTMLFormElement>(null)
  useLayoutEffect(() => {
    if (focus) form.current?.querySelector<HTMLInputElement>('input:not(:disabled), textarea')?.focus()
  }, [])
  useEffect(() => {
    let cancelled = false
    setReferenceError('')
    setLinked(null)
    if (!shownProjectId) return
    if (project?.id === shownProjectId) { setLinked(project); return }
    void roomRequest<ProjectEnvelope>(store.user.id, '/api/projects/' + encodeURIComponent(shownProjectId))
      .then(data => { if (!cancelled) setLinked(data.project) })
      .catch(e => { if (!cancelled) setReferenceError(e.message) })
    return () => { cancelled = true }
  }, [shownProjectId, project, store.user.id])
  const targets = linked ? referenceTargets(linked) : []
  const parentLabel = parent ? [...taskAncestors(store.tasks, parent.id).map(t => t.title), parent.title].join(' / ') : 'No parent'
  return <form ref={form} className="task-editor" data-tour="task-form" onSubmit={e => {
    e.preventDefault()
    void onSave({ title: title.trim(), details, scope, kind, parentId, assigneeId, dueDate, projectId, targetId, linkMode: task?.reference ? undefined : linkMode })
  }}>
    <label>{kind === 'group' ? 'Group name' : 'Task title'}
      <input aria-label={kind === 'group' ? 'Group name' : 'Task title'} value={title} required maxLength={200}
        disabled={!!task?.reference} onChange={e => setTitle(e.target.value)} placeholder={kind === 'group' ? 'Neighborhood, chapter, milestone…' : 'What is the next step?'} />
    </label>
    {task?.reference && <small>The name and story link follow your writing. You can organize this task freely.</small>}
    <div className="task-editor-grid">
      <label>Visibility<select aria-label="Task visibility" value={scope} disabled={!!task || !!draft.parent}
        onChange={e => { setScope(e.target.value as typeof scope); setParentId('') }}>
        <option value="team">Team</option><option value="personal">Personal · only you</option>
      </select></label>
      <div className="task-parent-choice"><span>Parent (optional)</span><button type="button" className="button" onClick={() => setChooseParent(true)}>Choose parent</button><small>{parentLabel}</small></div>
    </div>
    <details open={task ? true : undefined}>
      <summary>Notes, owner, due date & story link</summary>
      <label>Task notes<textarea rows={3} value={details} maxLength={10000} onChange={e => setDetails(e.target.value)} /></label>
      {kind === 'task' && <div className="task-editor-grid">
        <label>Due date<input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} /></label>
        {scope === 'team' && <label>Assigned to<select value={assigneeId} onChange={e => setAssigneeId(e.target.value)}>
          <option value="">Unassigned</option>
          {store.members.map(m => <option key={m.id} value={m.id}>{m.displayName}</option>)}
        </select></label>}
      </div>}
      <div className="task-editor-grid">
        <label>Story<select value={shownProjectId} disabled={!!task?.reference || (inherited && !!parentId)}
          onChange={e => { setProjectId(e.target.value); setTargetId(''); setLinkMode('manual') }}>
          <option value="">No story link</option>
          {shownProjectId && !projects.some(p => p.id === shownProjectId) && <option value={shownProjectId}>Story no longer available</option>}
          {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select></label>
        {shownProjectId && <label>Linked entry or scene<select value={shownTargetId} disabled={!!task?.reference || !linked || (inherited && !!parentId)}
          onChange={e => setTargetId(e.target.value)}>
          <option value="">Whole story</option>
          {shownTargetId && !targets.some(t => t.id === shownTargetId) && <option value={shownTargetId}>Reference no longer available</option>}
          {targets.map(t => <option key={t.id} value={t.id}>{t.title} · {t.section}</option>)}
        </select></label>}
      </div>
      {!task?.reference && <label className="task-inherit-link"><input type="checkbox" checked={linkMode === 'inherit'} onChange={e => {
        setLinkMode(e.target.checked ? 'inherit' : 'manual')
        if (!e.target.checked && parent) { setProjectId(parent.projectId); setTargetId(parent.targetId) }
      }} />Follow parent’s story link <small>Subtasks follow automatically. Turn this off to choose a separate link.</small></label>}
      {referenceError && <p className="task-editor-error" role="status">{referenceError} The existing task link is kept.</p>}
    </details>
    {error && <p role="alert" className="task-editor-error">{error}</p>}
    <div className="task-editor-actions">
      <button type="button" className="button" disabled={store.busy} onClick={onCancel}>Cancel</button>
      <button type="submit" className="button button--primary" disabled={store.busy || !title.trim()}>
        {store.busy ? 'Saving…' : task ? 'Save task' : kind === 'group' ? 'Create group' : 'Add task'}
      </button>
    </div>
    {chooseParent && <TaskOrganizer source={{ ...(task || draftTask(title.trim() || (kind === 'group' ? 'New group' : 'New task'), scope, store.user.id)), title: title.trim() || 'Untitled task', parentId }}
      onClose={() => setChooseParent(false)} onChoose={id => { setParentId(id); setChooseParent(false) }} />}
  </form>
}
